import { useState, useEffect } from 'react';
import { reportsAPI, objectsAPI, usersAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { formatDurationShort, getMonthStartDate, getMonthEndDate } from '@/lib/utils';
import { 
  FileBarChart, 
  Download, 
  Filter, 
  Loader2,
  Clock,
  AlertTriangle,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

// Download blob - works in browser; on native, opens URL in system browser
function downloadBlob(blob, filename, directUrl) {
  // In Capacitor WebView, use direct URL with system browser
  if (window.Capacitor?.isNativePlatform?.() && directUrl) {
    window.open(directUrl, '_system');
    return;
  }
  // Standard browser download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 1000);
}

export default function Reports() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [objects, setObjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [docLanguage, setDocLanguage] = useState(language); // Language for exported documents
  const [filters, setFilters] = useState({
    user_id: 'all-employees',
    object_id: 'all-objects',
    start_date: getMonthStartDate(),
    end_date: getMonthEndDate()
  });
  
  const DOC_LANGUAGES = [
    { code: 'et', label: 'Eesti', flag: '🇪🇪' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
    { code: 'en', label: 'English', flag: '🇬🇧' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [objectsRes, usersRes] = await Promise.all([
        objectsAPI.list(),
        usersAPI.list()
      ]);
      setObjects(objectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const payload = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      if (filters.user_id && filters.user_id !== 'all-employees') payload.user_id = filters.user_id;
      if (filters.object_id && filters.object_id !== 'all-objects') payload.object_id = filters.object_id;

      const res = await reportsAPI.daily(payload);
      setReports(res.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const exportReportCsv = () => {
    if (reports.length === 0) {
      toast.error(t('reports.noDataToExport'));
      return;
    }

    const getHeaders = () => {
      const headers = {
        et: ['Töötaja', 'Objekt', 'Kuupäev', 'Tööaeg', 'Paus', 'Lõuna', 'Väljas', 'GPS puudub', 'Rikkumised'],
        ru: ['Сотрудник', 'Объект', 'Дата', 'Рабочее время', 'Пауза', 'Обед', 'За зоной', 'Нет GPS', 'Нарушения'],
        en: ['Employee', 'Object', 'Date', 'Working Time', 'Pause', 'Lunch', 'Outside', 'No GPS', 'Violations']
      };
      return headers[docLanguage] || headers.en;
    };

    const rows = reports.map(r => [
      r.user_name,
      r.object_name,
      r.date,
      formatDurationShort(r.working_seconds),
      formatDurationShort(r.pause_seconds),
      formatDurationShort(r.lunch_seconds),
      formatDurationShort(r.outside_seconds),
      formatDurationShort(r.no_gps_seconds),
      r.violations_count
    ]);

    const csvContent = [getHeaders(), ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `workplus_report_${filters.start_date}_${filters.end_date}.csv`);

    toast.success('Report exported successfully');
  };

  const exportTimesheet = async () => {
    setExporting(true);
    try {
      const date = new Date(filters.end_date || filters.start_date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const objectIds = filters.object_id !== 'all-objects' ? filters.object_id : null;
      const userIds = filters.user_id !== 'all-employees' ? filters.user_id : null;
      
      // Build direct URL for native download
      const token = localStorage.getItem('workplus_token');
      const params = new URLSearchParams({ year, month, lang: docLanguage });
      if (objectIds) params.set('object_ids', objectIds);
      if (userIds) params.set('user_ids', userIds);
      if (token) params.set('token', token);
      const directUrl = `${window.location.origin}/api/reports/timesheet-export?${params}`;
      
      const response = await reportsAPI.exportTimesheet(year, month, objectIds, userIds, docLanguage);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(blob, `timesheet_${year}_${month.toString().padStart(2, '0')}.csv`, directUrl);
      
      toast.success(t('msg.exportSuccess'));
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('msg.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const exportTimesheetExcel = async () => {
    setExporting(true);
    try {
      const date = new Date(filters.end_date || filters.start_date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const objectId = filters.object_id !== 'all-objects' ? filters.object_id : null;

      // Build direct URL for native download
      const token = localStorage.getItem('workplus_token');
      const params = new URLSearchParams({ year, month, apply_shift_rules: 'true', lang: docLanguage });
      if (objectId) params.set('object_id', objectId);
      if (token) params.set('token', token);
      const directUrl = `${window.location.origin}/api/reports/timesheet-excel?${params}`;

      const response = await reportsAPI.exportTimesheetExcel(year, month, objectId, true, docLanguage);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      downloadBlob(blob, `timesheet_${year}_${month.toString().padStart(2, '0')}.xlsx`, directUrl);
      
      toast.success(t('msg.exportSuccess'));
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('msg.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const totals = reports.reduce((acc, r) => ({
    working: acc.working + (r.working_seconds || 0),
    pause: acc.pause + (r.pause_seconds || 0),
    lunch: acc.lunch + (r.lunch_seconds || 0),
    outside: acc.outside + (r.outside_seconds || 0),
    no_gps: acc.no_gps + (r.no_gps_seconds || 0),
    violations: acc.violations + (r.violations_count || 0)
  }), { working: 0, pause: 0, lunch: 0, outside: 0, no_gps: 0, violations: 0 });

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-white font-heading flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <FileBarChart className="h-6 w-6 text-wp-light" />
          </div>
          {t('reports.title')}
        </h1>
        <p className="text-wp-light/40 mt-2">{t('reports.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <Filter className="h-5 w-5 text-wp-light" />
          </div>
          <h2 className="text-lg font-semibold text-white font-heading">{t('reports.filters')}</h2>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('reports.startDate')}</Label>
            <Input
              type="date"
              lang={language}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              className="bg-wp-dark/30 border-wp-dark/50 text-white"
              data-testid="report-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('reports.endDate')}</Label>
            <Input
              type="date"
              lang={language}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              className="bg-wp-dark/30 border-wp-dark/50 text-white"
              data-testid="report-end-date"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('common.employee')}</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters({...filters, user_id: v})}>
              <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                <SelectValue placeholder={t('common.allEmployees')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-employees">{t('common.allEmployees')}</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-wp-light/70">{t('common.object')}</Label>
            <Select value={filters.object_id} onValueChange={(v) => setFilters({...filters, object_id: v})}>
              <SelectTrigger className="bg-wp-dark/30 border-wp-dark/50 text-white">
                <SelectValue placeholder={t('common.allObjects')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-objects">{t('common.allObjects')}</SelectItem>
                {objects.map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-3 mt-5 pt-5 border-t border-wp-dark/30">
          <button 
            onClick={loadReports} 
            disabled={loading} 
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-wp-medium hover:bg-wp-light text-white font-medium rounded-xl transition-all btn-glow disabled:opacity-50"
            data-testid="generate-report-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart className="h-4 w-4" />}
            {t('reports.generate')}
          </button>
          
          <div className="h-6 w-px bg-wp-dark/50 hidden sm:block" />
          
          {/* Document Language Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-wp-light/50 text-sm hidden sm:block">{t('reports.documentLanguage')}:</Label>
            <Select value={docLanguage} onValueChange={setDocLanguage}>
              <SelectTrigger className="w-[130px] h-9 bg-wp-dark/30 border-wp-dark/50 text-white text-sm" data-testid="doc-language-selector">
                <SelectValue>
                  {DOC_LANGUAGES.find(l => l.code === docLanguage)?.flag} {DOC_LANGUAGES.find(l => l.code === docLanguage)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-wp-darker border-wp-dark/50">
                {DOC_LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="h-6 w-px bg-wp-dark/50 hidden sm:block" />
          
          <button 
            onClick={exportReportCsv} 
            disabled={reports.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors disabled:opacity-50"
            data-testid="export-report-button"
            title={t('reports.exportReport')}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.download')}</span>
          </button>
          
          <button
            onClick={exportTimesheet}
            disabled={exporting}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-xl border border-emerald-500/30 transition-colors disabled:opacity-50"
            data-testid="export-timesheet-button"
            title={t('reports.exportTimesheetCSV')}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span>CSV</span>
          </button>
          
          <button
            onClick={exportTimesheetExcel}
            disabled={exporting}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-wp-medium/10 hover:bg-wp-medium/20 text-wp-light rounded-xl border border-wp-medium/30 transition-colors disabled:opacity-50"
            data-testid="export-timesheet-excel-button"
            title={t('reports.exportTimesheetExcel')}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span>Excel</span>
          </button>
        </div>
      </div>

      {/* Summary */}
      {reports.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: t('reports.working'), value: formatDurationShort(totals.working), icon: Clock, color: 'emerald' },
            { label: t('status.pause'), value: formatDurationShort(totals.pause), icon: Clock, color: 'amber' },
            { label: t('status.lunch'), value: formatDurationShort(totals.lunch), icon: Clock, color: 'cyan' },
            { label: t('status.outside'), value: formatDurationShort(totals.outside), icon: Clock, color: 'slate' },
            { label: t('reports.noGps'), value: formatDurationShort(totals.no_gps), icon: Clock, color: 'rose' },
            { label: t('dashboard.violations'), value: totals.violations, icon: AlertTriangle, color: 'rose' },
          ].map((stat) => (
            <div key={stat.label} className="glass-card rounded-2xl p-5 hover-lift stat-card" style={{ '--accent-color': `var(--${stat.color}-500)` }}>
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`h-4 w-4 text-${stat.color}-400`} />
                <span className="text-xs text-wp-light/40">{stat.label}</span>
              </div>
              <p className={`text-2xl font-bold font-heading text-${stat.color}-400`}>{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Results Table */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-wp-dark/50 hover:bg-transparent">
                <TableHead className="text-wp-light/50">{t('common.employee')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.object')}</TableHead>
                <TableHead className="text-wp-light/50">{t('common.date')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('reports.working')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('status.pause')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('status.lunch')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('status.outside')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('reports.noGps')}</TableHead>
                <TableHead className="text-right text-wp-light/50">{t('dashboard.violations')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-wp-light/40">
                    {loading ? t('common.loading') : t('reports.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report, idx) => (
                  <TableRow key={idx} className="border-wp-dark/30 hover:bg-wp-dark/30" data-testid={`report-row-${idx}`}>
                    <TableCell className="font-medium text-white">{report.user_name}</TableCell>
                    <TableCell className="text-wp-light/70">{report.object_name}</TableCell>
                    <TableCell className="text-wp-light/70">{report.date}</TableCell>
                    <TableCell className="text-right text-emerald-400 font-medium font-mono">
                      {formatDurationShort(report.working_seconds)}
                    </TableCell>
                    <TableCell className="text-right text-amber-400 font-mono">
                      {formatDurationShort(report.pause_seconds)}
                    </TableCell>
                    <TableCell className="text-right text-wp-light font-mono">
                      {formatDurationShort(report.lunch_seconds)}
                    </TableCell>
                    <TableCell className="text-right text-wp-400 font-mono">
                      {formatDurationShort(report.outside_seconds)}
                    </TableCell>
                    <TableCell className="text-right text-rose-400 font-mono">
                      {formatDurationShort(report.no_gps_seconds)}
                    </TableCell>
                    <TableCell className="text-right">
                      {report.violations_count > 0 && (
                        <span className="text-rose-400 font-medium">{report.violations_count}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
